import type { UniclientFrameType } from '@lemonade-hq/uniclient-chat';
import type { ResponsiveValues } from '@lemonade-hq/uniclient-theme';
import { Flex, TypingTickerCursor, useResponsiveValue } from '@lemonade-hq/uniclient-theme';
import type { PropsWithChildren } from 'react';
import React from 'react';
import { HISTORY_MAIN_PADDING, HISTORY_OUTPUT_TRANSLATE_Y } from '../elements/WizardHistory.css';
import { HEADER_WITH_MARGIN } from '../elements/WizardMain';
import { FRAME_TRANSITION_DURATION } from './durations';
import { SlideYAnimator } from './SlideYAnimator';

// TODO: use motion now that it supports css vars animations in calc
const STEP_TRANSLATION = {
  sm: `calc(-100% - ${HISTORY_MAIN_PADDING} + ${HISTORY_OUTPUT_TRANSLATE_Y.sm})`,
  lg: `calc(-100% - ${HISTORY_MAIN_PADDING} + ${HISTORY_OUTPUT_TRANSLATE_Y.lg})`,
} satisfies ResponsiveValues<string>;

// TODO: use motion now that it supports css vars animations in calc
const OUT_OF_SCREEN_TRANSLATION = {
  sm: `calc(-100% - ${HEADER_WITH_MARGIN.sm}px)`,
  lg: `calc(-100% - ${HEADER_WITH_MARGIN.lg}px)`,
} satisfies ResponsiveValues<string>;

interface WizardStepsAnimatorProps {
  readonly isTransitioningOut: boolean;
  readonly stepId?: string;
  readonly onAnimationEnd?: () => void;
  readonly outOfScreen?: boolean;
  readonly lastTransitioningFrameType?: UniclientFrameType;
}

/**
 * Animates the steps of the wizard.
 * The animation is triggered by changing the `currentStepIndex` prop.
 * The animation is a slide from 0 to -100%.
 * The animation is reversed when the `currentStepIndex` decreases.
 * On reverse, the animation is only fading out.
 */
export const WizardFrameStepAnimator: React.FC<PropsWithChildren<WizardStepsAnimatorProps>> = ({
  isTransitioningOut,
  children,
  stepId,
  onAnimationEnd,
  outOfScreen,
  lastTransitioningFrameType,
}) => {
  const translation = useResponsiveValue(outOfScreen ? OUT_OF_SCREEN_TRANSLATION : STEP_TRANSLATION);
  const shouldDisplayTypingTicker = lastTransitioningFrameType === 'message';

  return (
    <Flex position="relative" width="100%">
      <SlideYAnimator
        animationKey={stepId}
        duration={FRAME_TRANSITION_DURATION}
        emptyComponent={shouldDisplayTypingTicker && <TypingTickerCursor blinking />}
        isTransitioningOut={isTransitioningOut}
        onExitComplete={onAnimationEnd}
        translate={translation}
      >
        {children}
      </SlideYAnimator>
    </Flex>
  );
};
